import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { getGuestXp, clearGuestXp, steamLoginHref } from './xp';

const CreateAccount = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: ''
  });
  const [googleId, setGoogleId] = useState('');
  const [avatar, setAvatar] = useState('');
  const [returnTo, setReturnTo] = useState('/');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const isValidReturnTo = (url) => {
    if (!url || typeof url !== 'string') return false;
    if (!url.startsWith('/') || url.startsWith('//')) return false;
    if (url.includes('@')) return false;
    if (url.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/)) return false;
    if (url.includes('\\')) return false;
    return true;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleIdParam = params.get('googleId');
    const avatarParam = params.get('avatar');
    const nameParam = params.get('name');
    const emailParam = params.get('email');
    const returnToParam = params.get('returnTo');

    if (!googleIdParam) {
      navigate('/');
      return;
    }

    setGoogleId(googleIdParam);
    if (avatarParam) setAvatar(avatarParam);
    if (emailParam) {
      setFormData((prev) => ({ ...prev, email: emailParam }));
    }
    if (nameParam) {
      setFormData((prev) => ({
        ...prev,
        username: nameParam.replace(/[^a-zA-Z0-9]/g, '').slice(0, 16)
      }));
    }
    if (returnToParam) {
      const validatedReturnTo = isValidReturnTo(returnToParam) ? returnToParam : '/';
      setReturnTo(validatedReturnTo);
    }

    let cancelled = false;
    axios.get('/api/auth/status', { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const user = res.data?.user;
        if (user?.pendingGoogleLink) {
          navigate('/connect-google', { replace: true });
          return;
        }
        if (res.data?.isAuthenticated && user && !user.newUser && user.userId) {
          const dest = isValidReturnTo(returnToParam) ? returnToParam : '/';
          navigate(dest, { replace: true });
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [location, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3 || formData.username.length > 16) {
      newErrors.username = 'Username must be between 3 and 16 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 254) {
      newErrors.email = 'Email must be less than 254 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setErrors({});

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        const response = await axios.post('/api/auth/create-user', {
          username: formData.username,
          email: formData.email,
          googleId: String(googleId),
          guestXp: getGuestXp()
        }, { withCredentials: true });

        if (response.status === 201) {
          if (response.data?.guestXpMerged) {
            clearGuestXp();
          }
          navigate(returnTo);
        }
      } catch (error) {
        console.error('Account creation error:', error.response?.data || error.message);

        if (error.response?.data) {
          const { error: errorMessage, details } = error.response.data;

          if (details) {
            const newErrors = {};
            Object.entries(details).forEach(([field, message]) => {
              if (message) {
                newErrors[field] = Array.isArray(message) ? message[0]?.message || message : message;
              }
            });
            setErrors(newErrors);
          }

          setServerError(errorMessage || 'Failed to create account. Please try again.');
        } else {
          setServerError('Server error. Please try again later.');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const claimHref = steamLoginHref(returnTo);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Create Your Account</h2>
          <p className="text-gray-600">Pick a username. Email comes from Google.</p>
        </div>

        {avatar && (
          <div className="flex justify-center mb-6">
            <img
              src={avatar}
              alt="Google avatar"
              className="w-20 h-20 rounded-full border-4 border-blue-500"
            />
          </div>
        )}

        {serverError && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg text-gray-900 ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Choose a username"
              maxLength={16}
            />
            {errors.username && (
              <p className="text-red-500 text-xs mt-1">{errors.username}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              readOnly={!!formData.email}
              className={`w-full px-3 py-2 border rounded-lg text-gray-900 ${errors.email ? 'border-red-500' : 'border-gray-300'} ${formData.email ? 'bg-gray-100' : ''}`}
              placeholder="Your email address"
              maxLength={254}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            style={{ backgroundColor: '#2563eb' }}
            className={`w-full hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          Already have a BIGPLAYS account from Steam?
          {' '}
          <a href={claimHref} className="text-blue-600 hover:underline">
            Link it with Steam
          </a>
          <span className="block mt-1 text-xs text-gray-400">
            Skip this form — Steam will keep your existing username.
          </span>
        </p>
        <p className="text-center text-xs text-gray-400 mt-2">
          By creating an account you agree to the{' '}
          <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">Terms</Link>
          .
        </p>
        <p className="text-center text-xs text-gray-400 mt-2">
          <Link to="/report" className="hover:underline">Report content</Link>
        </p>
        <p className="text-center text-xs text-gray-400 mt-2">
          <Link to="/" className="hover:underline">Cancel</Link>
        </p>
      </div>
    </div>
  );
};

export default CreateAccount;
